import type { Vector2d } from '@/lib/CanvasEngine/types'
import type { IGroup, IRect, IRenderer, IText } from '@/lib/CanvasEngine/backend/IRenderer'

/**
 * Balloon Tag 설정 상수
 */
const TAG_CONFIG = {
  MARGIN_LEFT: 6,
  PADDING_LEFT: 6,
  PADDING_HORIZONTAL: 12,
  PADDING_VERTICAL: 10,
  TEXT_SPACING: 2,
  FONT_SIZE: 14 * 2.5,
} as const

/**
 * Balloon Tag 데이터 인터페이스
 */
export interface BalloonTagData {
  top: string
  bottom: string
}

/**
 * Balloon Tag 클래스
 * Balloon 옆에 표시되는 태그 요소를 관리
 */
export class BalloonTag {
  public group: IGroup
  // 태그는 setData 전엔 숨김·빈 내용이라 요소를 그때 만든다(결함마다 Rect+Text 2개 — 층 진입 비용)
  private rect: IRect | undefined
  private topText!: IText
  private bottomText!: IText

  constructor(private readonly renderer: IRenderer) {
    this.group = renderer.createGroup({ visible: false })
  }

  /**
   * Tag 요소들 초기화
   */
  private initElements(): IRect {
    this.rect = this.renderer.createRect({
      fill: 'transparent',
      stroke: 'transparent',
    })

    this.topText = this.createText('tagTextTop')
    this.bottomText = this.createText('tagTextBottom')

    this.group.add(this.rect, this.topText, this.bottomText)
    return this.rect
  }

  /**
   * Text 요소 생성
   */
  private createText(name: string): IText {
    return this.renderer.createText({
      name,
      text: '',
      fontSize: TAG_CONFIG.FONT_SIZE,
      fill: '#000',
      align: 'left',
      listening: false,
    })
  }

  /**
   * Tag 데이터 업데이트
   */
  public setData(tag: BalloonTagData): void {
    if (!this.rect) this.initElements()
    this.topText.text(tag.top)
    this.bottomText.text(tag.bottom)
    this.topText.visible(tag.top !== '')
    this.bottomText.visible(tag.bottom !== '')

    this.updateLayout()
    this.group.visible(true)
  }

  /**
   * Tag 레이아웃 업데이트
   */
  private updateLayout(): void {
    const { PADDING_LEFT, PADDING_VERTICAL, TEXT_SPACING, PADDING_HORIZONTAL } = TAG_CONFIG

    // Text 위치 설정
    this.topText.position({ x: PADDING_LEFT, y: PADDING_VERTICAL })
    this.bottomText.position({
      x: PADDING_LEFT,
      y: PADDING_VERTICAL + this.topText.measure().height + TEXT_SPACING,
    })

    // Rect 크기 설정
    const topSize = this.topText.measure()
    const bottomSize = this.bottomText.measure()
    const maxWidth = Math.max(topSize.width, bottomSize.width)
    const totalHeight = topSize.height + bottomSize.height + TEXT_SPACING + PADDING_VERTICAL * 2

    this.rect!.size({
      width: maxWidth + PADDING_HORIZONTAL,
      height: totalHeight,
    })
  }

  /**
   * Tag 위치를 Balloon 기준으로 동기화
   */
  public syncPosition(balloonPos: Vector2d, balloonSize: number): void {
    const tagHeight = this.rect?.height() ?? 0
    this.group.x(balloonPos.x + balloonSize / 2 + TAG_CONFIG.MARGIN_LEFT)
    this.group.y(balloonPos.y - tagHeight / 2)
  }

  /**
   * Tag 숨김
   */
  public hide(): void {
    this.group.visible(false)
  }

  /**
   * Tag 표시
   */
  public show(): void {
    this.group.visible(true)
  }

  /**
   * Tag가 표시 중인지 확인
   */
  public isVisible(): boolean {
    return this.group.visible()
  }

  /**
   * Tag 높이 가져오기
   */
  public getHeight(): number {
    return this.rect?.height() ?? 0
  }

  /**
   * Tag 너비 가져오기
   */
  public getWidth(): number {
    return this.rect?.width() ?? 0
  }
}
